/**
 * Homepage testimonials sourced exclusively from the business's real Google
 * Business Profile reviews (synced daily server-side). Renders NOTHING until
 * genuine reviews exist — no placeholder or fabricated content, ever.
 */
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Star, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex gap-1" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={
            i < rating
              ? "w-4 h-4 fill-accent text-accent"
              : "w-4 h-4 text-muted-foreground/30"
          }
        />
      ))}
    </div>
  );
}

export default function GoogleTestimonialsSection() {
  const { data } = trpc.reviews.googleList.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
  });

  const reviews = data?.reviews ?? [];
  const profile = data?.profile;
  const reviewFormUrl = data?.reviewFormUrl ?? null;

  // With no reviews AND no review link configured, render nothing at all.
  if (reviews.length === 0 && !reviewFormUrl) return null;

  // No reviews yet but a review link exists: show a compact, honest
  // "be the first" CTA band instead of an empty testimonials grid.
  if (reviews.length === 0 && reviewFormUrl) {
    return (
      <section className="py-16 bg-slate-50" id="reviews">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-3 text-foreground">
              Had a Great Experience With Us?
            </h2>
            <p className="text-foreground/70 mb-6">
              Your feedback helps neighbors find reliable septic service.
              It only takes a minute — and it means the world to our
              veteran-owned team.
            </p>
            <Button
              asChild
              size="lg"
              className="text-base font-semibold shadow-md"
            >
              <a
                href={reviewFormUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <PenLine className="w-5 h-5 mr-2" />
                Leave a Review
              </a>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-slate-50" id="reviews">
      <div className="container">
        <h2 className="text-4xl font-bold mb-4 text-center text-foreground">
          What Our Customers Say
        </h2>
        {profile?.rating != null && profile.userRatingsTotal > 0 && (
          <div className="flex items-center justify-center gap-2 mb-12">
            <Stars rating={Math.round(profile.rating)} />
            <span className="text-foreground font-semibold">
              {profile.rating.toFixed(1)}
            </span>
            <span className="text-foreground/60 text-sm">
              ({profile.userRatingsTotal} Google review
              {profile.userRatingsTotal === 1 ? "" : "s"})
            </span>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.slice(0, 6).map((review) => (
            <Card key={review.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  {review.authorPhotoUrl ? (
                    <img
                      src={review.authorPhotoUrl}
                      alt=""
                      width={36}
                      height={36}
                      className="w-9 h-9 rounded-full"
                      loading="lazy"
                      decoding="async"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-accent/20 flex items-center justify-center text-accent-text font-semibold">
                      {review.authorName.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-foreground leading-tight">
                      {review.authorName}
                    </h3>
                    {review.relativeTime && (
                      <p className="text-xs text-foreground/50">
                        {review.relativeTime} · Google
                      </p>
                    )}
                  </div>
                </div>
                <Stars rating={review.rating} />
              </CardHeader>
              <CardContent>
                <p className="text-foreground/70 text-sm">{review.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="flex items-center justify-center gap-4 mt-10">
          {reviewFormUrl && (
            <Button
              asChild
              size="lg"
              className="text-base font-semibold shadow-md"
            >
              <a
                href={reviewFormUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <PenLine className="w-5 h-5 mr-2" />
                Leave a Review
              </a>
            </Button>
          )}
          {profile?.googleMapsUrl && (
            <a
              href={profile.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary underline underline-offset-4 hover:text-primary/80"
            >
              See all reviews on Google
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
